import { CheckCircle2, RefreshCw, Users, ArrowRightLeft, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RecurrenceLegend from './RecurrenceLegend';
import { RecurrenceConfig } from '../../types';

/**
 * L'échéancier reste configurable même sur la dernière échéance (isRecurring=false),
 * afin de pouvoir modifier le nombre total de paiements depuis n'importe quelle occurrence.
 * Exporté pour que les vues qui n'affichent pas le cluster complet (résumé des cartes
 * mobiles) proposent le même accès à la configuration.
 */
export function canConfigureRecurrence(
  isRecurring: boolean,
  recurrenceConfig: RecurrenceConfig | null | undefined,
): boolean {
  return isRecurring || (recurrenceConfig?.frequency === 'INSTALLMENTS' && !!recurrenceConfig?.totalCount);
}

interface OperationStatusClusterProps {
  isValidated: boolean;
  isRecurring: boolean;
  includeInCoupleIncome: boolean;
  isSharedExpense?: boolean;
  recurrenceConfig: RecurrenceConfig | null | undefined;
  operationDate: string;
  operationType: 'credit' | 'debit';
  onToggleValidated: () => void;
  onToggleRecurring: () => void;
  onToggleCoupleIncome: () => void;
  onToggleSharedExpense?: () => void;
  onOpenRecurrenceConfig: () => void;
  onFocusStop?: (e: React.FocusEvent) => void;
  showCoupleIncome?: boolean;
  showSharedExpense?: boolean;
  disabled?: boolean;
  variant?: 'desktop' | 'mobile';
  compact?: boolean;
}

export default function OperationStatusCluster({
  isValidated,
  isRecurring,
  includeInCoupleIncome,
  isSharedExpense = false,
  recurrenceConfig,
  operationDate,
  operationType,
  onToggleValidated,
  onToggleRecurring,
  onToggleCoupleIncome,
  onToggleSharedExpense,
  onOpenRecurrenceConfig,
  onFocusStop,
  showCoupleIncome = false,
  showSharedExpense = false,
  disabled = false,
  variant = 'desktop',
  compact = false,
}: OperationStatusClusterProps) {
  const { t } = useTranslation('common');
  const stopFocus = (e: React.FocusEvent) => {
    e.stopPropagation();
    onFocusStop?.(e);
  };

  const isInstallmentSeries = recurrenceConfig?.frequency === 'INSTALLMENTS' && !!recurrenceConfig?.totalCount;
  const showRecurrenceLegend = isRecurring || isInstallmentSeries;
  const showRecurrenceConfig = canConfigureRecurrence(isRecurring, recurrenceConfig);

  // Mobile variant OR desktop non-compact: pills with text
  if (variant === 'mobile' || (variant === 'desktop' && !compact)) {
    // Desktop avec 4 pills (showSharedExpense) → grid 2 colonnes
    // Desktop avec 2-3 pills → flex nowrap 1 ligne
    // Mobile → flex wrap
    const isDesktopGrid = variant === 'desktop' && showSharedExpense;
    const pillsClassName = variant === 'mobile'
      ? 'flex items-center gap-2 flex-wrap'
      : isDesktopGrid
        ? 'grid grid-cols-2 gap-1.5 [&>*:nth-child(odd)]:justify-self-end [&>*:nth-child(even)]:justify-self-start'
        : 'flex items-center gap-1.5 flex-nowrap';

    return (
      <div>
      <div className={pillsClassName}>
        {/* Validé pill */}
        <button
          type="button"
          onClick={onToggleValidated}
          onFocus={stopFocus}
          disabled={disabled}
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
            isValidated
              ? 'bg-primary-100 text-primary-700'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t('statusCluster.validated')}
        </button>

        {/* Revenus couple pill */}
        {showCoupleIncome && (
          <button
            type="button"
            onClick={() => operationType === 'credit' && onToggleCoupleIncome()}
            onFocus={stopFocus}
            disabled={disabled || operationType !== 'credit'}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              operationType !== 'credit'
                ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                : includeInCoupleIncome
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            {t('statusCluster.income')}
          </button>
        )}

        {/* Dépense partagée pill */}
        {showSharedExpense && (
          <button
            type="button"
            onClick={() => operationType === 'debit' && onToggleSharedExpense?.()}
            onFocus={stopFocus}
            disabled={disabled || operationType !== 'debit'}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              operationType !== 'debit'
                ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                : isSharedExpense
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            {t('statusCluster.shared')}
          </button>
        )}

        {/* Récurrent pill + gear */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleRecurring}
            onFocus={stopFocus}
            disabled={disabled}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              isRecurring
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t('statusCluster.recurring')}
          </button>
          {showRecurrenceConfig && (
            <button
              type="button"
              onClick={onOpenRecurrenceConfig}
              onFocus={stopFocus}
              className={`rounded-full transition-colors ${
                variant === 'mobile'
                  ? 'p-1.5 bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-primary-600'
                  : 'p-1 text-gray-500 hover:bg-gray-100'
              }`}
              title={t('statusCluster.configureRecurrence')}
              aria-label={t('statusCluster.configureRecurrence')}
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

      </div>
      {showRecurrenceLegend && (
        <div className="mt-1">
          <RecurrenceLegend config={recurrenceConfig} operationDate={operationDate} />
        </div>
      )}
      </div>
    );
  }

  // Desktop compact variant: icon buttons only
  return (
    <div>
      <div className="flex items-center gap-1">
        {/* Validé */}
        <button
          type="button"
          title={isValidated ? t('statusCluster.validatedClickInvalidate') : t('statusCluster.notValidatedClickValidate')}
          onClick={onToggleValidated}
          onFocus={stopFocus}
          disabled={disabled}
          className={`p-1 rounded transition-colors ${
            isValidated
              ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
              : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
        </button>

        {/* Récurrent */}
        <button
          type="button"
          title={isRecurring ? t('statusCluster.recurringClickDisable') : t('statusCluster.notRecurringClickEnable')}
          onClick={onToggleRecurring}
          onFocus={stopFocus}
          disabled={disabled}
          className={`p-1 rounded transition-colors ${
            isRecurring
              ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
              : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
          }`}
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        {/* Configuration de la récurrence — bouton plein format : en badge superposé
            il était illisible et intouchable au doigt sur tablette. */}
        {showRecurrenceConfig && (
          <button
            type="button"
            onClick={onOpenRecurrenceConfig}
            onFocus={stopFocus}
            className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            title={t('statusCluster.configureRecurrence')}
            aria-label={t('statusCluster.configureRecurrence')}
          >
            <Settings2 className="w-4 h-4" />
          </button>
        )}

        {/* Revenus couple */}
        {showCoupleIncome && (
          <button
            type="button"
            title={t('statusCluster.includeInSharedIncome')}
            onClick={() => operationType === 'credit' && onToggleCoupleIncome()}
            onFocus={stopFocus}
            disabled={disabled || operationType !== 'credit'}
            className={`p-1 rounded transition-colors ${
              operationType !== 'credit'
                ? 'text-gray-200 cursor-not-allowed'
                : includeInCoupleIncome
                  ? 'text-purple-600 bg-purple-50 hover:bg-purple-100'
                  : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" />
          </button>
        )}

        {/* Dépense partagée */}
        {showSharedExpense && (
          <button
            type="button"
            title={t('statusCluster.sharedExpense')}
            onClick={() => operationType === 'debit' && onToggleSharedExpense?.()}
            onFocus={stopFocus}
            disabled={disabled || operationType !== 'debit'}
            className={`p-1 rounded transition-colors ${
              operationType !== 'debit'
                ? 'text-gray-200 cursor-not-allowed'
                : isSharedExpense
                  ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                  : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
            }`}
          >
            <ArrowRightLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {showRecurrenceLegend && (
        <div className="mt-1">
          <RecurrenceLegend config={recurrenceConfig} operationDate={operationDate} className="max-w-[140px]" />
        </div>
      )}
    </div>
  );
}
